import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import UserComment from '@/store/models/user/UserComment'
import UserLike from '@/store/models/user/UserLike'
import { withLatestReplies, adjustParentRepliesCount, removeCommentThreadLocally, applyCommentLikeState } from '@/utils/comment-orm'

export default {
  name: 'collectionItem',
  namespaced: true,
  actions: {
    // ==================== UPDATE ====================
    async edit(_, { item, data }) {
      const result = await this.$api.collectionItem.edit(item.ownerId, item.id, data)
      await CollectionItem.update({ where: item.id, data: result })
      return result
    },

    async editThumbnail(_, { item, data }) {
      const { data: result } = await this.$api.collectionItem.editThumbnail(item.ownerId, item.id, data, {
        params: { imageType: 'image' },
      })
      await CollectionItem.update({ where: item.id, data: { image: result } })
      return result
    },

    // File items: toggle the uploader's public link to the original (D17); resolves { fileShareUrl }
    async setFilePublic(_, { item, filePublic }) {
      const result = await this.$api.collectionItem.setFilePublic(item.ownerId, item.id, filePublic)
      await CollectionItem.update({ where: item.id, data: { fileShareUrl: result.fileShareUrl ?? null } })
      return result
    },

    async editTags({ commit }, { item, data }) {
      const result = await this.$api.collectionItem.editTags(item.ownerId, item.id, data)

      // NOTE we do not delete result.deletedTagIds from local store, as we still want to show them
      // inside things like the tag filter dropdown, even if they no longer exist in the collection

      // NOTE we don't update item here because we need to refetch item to get proper tag population

      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    async toggleFavorite(_, item) {
      try {
        await item.setLoading('favorite', true)
        const result = await this.$api.collectionItem.edit(item.ownerId, item.id, {
          isFavorite: !item.isFavorite,
        })

        await CollectionItem.update({ where: item.id, data: result })

        return result
      } finally {
        await item.setLoading('favorite', false)
      }
    },

    // ==================== DELETE ====================
    async delete(_, item) {
      try {
        await item.setLoading('remove', true)
        await this.$api.collectionItem.remove(item.ownerId, item.id)
        await CollectionItem.delete(item.id)

        return true
      } finally {
        await item.setLoading('remove', false)
      }
    },

    async fullDelete(_, item) {
      try {
        await item.setLoading('remove', true)
        await this.$api.collectionItem.removeFull(item.ownerId, item.id)
        await CollectionItem.delete(item.id)

        return true
      } finally {
        await item.setLoading('remove', false)
      }
    },

    async restore(_, item) {
      try {
        await item.setLoading('restore', true)
        await this.$api.collectionItem.restore(item.ownerId, item.id)
        await CollectionItem.delete(item.id)

        return true
      } finally {
        await item.setLoading('restore', false)
      }
    },

    // ==================== COMMENTS ====================
    async getComments(_, { item, data }) {
      const { results, next } = await this.$api.collectionItem.comments(item.ownerId, item.id, data)
      await UserComment.insertOrUpdate({ data: withLatestReplies(results) })

      return { results, next }
    },

    async getCommentReplies(_, { item, commentId, data }) {
      const { results, next } = await this.$api.collectionItem.comments(item.ownerId, item.id, {
        ...data,
        params: { ...data?.params, parent: commentId },
      })
      await UserComment.insertOrUpdate({ data: results })

      return { results, next }
    },

    async comment(_, { item, data }) {
      const comment = await this.$api.collectionItem.comment(item.ownerId, item.id, data)
      await UserComment.insert({ data: comment })
      await adjustParentRepliesCount(comment.parentId, 1)
      await CollectionItem.update({ where: item.id, data: { commentsCount: (item.commentsCount || 0) + 1 } })

      return comment
    },

    async removeComment(_, { item, commentId }) {
      await this.$api.collectionItem.removeComment(item.ownerId, item.id, commentId)
      const comment = await removeCommentThreadLocally(commentId)
      // a deleted top-level comment takes its replies with it
      const removedCount = 1 + (comment?.repliesCount || 0)
      await CollectionItem.update({
        where: item.id,
        data: { commentsCount: Math.max(0, (item.commentsCount || 0) - removedCount) },
      })
    },

    async setCommentsSeen(_, { item }) {
      try {
        await this.$api.collectionItem.setCommentsSeen(item.ownerId, item.id)
      } catch (error) {
        console.error(error)
      }
    },

    // ==================== LIKES ====================
    async getLikes(_, { item, data }) {
      const { results, next } = await this.$api.collectionItem.likes(item.ownerId, item.id, data)
      await UserLike.insertOrUpdate({ data: results })
      return { results, next }
    },

    async toggleLike(_, { item }) {
      try {
        await item.setLoading('like', true)
        if (!item.isLikedByUser) {
          const like = await this.$api.collectionItem.like(item.ownerId, item.id)
          await UserLike.insert({ data: like })
          await CollectionItem.update({
            where: item.id,
            data: {
              likes: item.likes + 1,
            },
          })
        } else {
          await this.$api.collectionItem.removeLike(item.ownerId, item.id)
          await UserLike.delete(record => record.resourceId === item.id)
          CollectionItem.update({
            where: item.id,
            data: {
              likes: item.likes - 1,
            },
          })
        }
        await CollectionItem.update({ where: item.id, data: { isLikedByUser: !item.isLikedByUser } })
      } finally {
        await item.setLoading('like', false)
      }
    },

    // ==================== COMMENT LIKES ====================
    async getCommentLikes(_, { item, commentId, data }) {
      const { results, next } = await this.$api.collectionItem.commentLikes(item.ownerId, item.id, commentId, data)
      await UserLike.insertOrUpdate({ data: results })
      return { results, next }
    },

    async toggleCommentLike({ rootGetters }, { item, comment }) {
      if (!comment.isLikedByUser) {
        const like = await this.$api.collectionItem.likeComment(item.ownerId, item.id, comment.id)
        await UserLike.insert({ data: like })
        await applyCommentLikeState(comment, true)
      } else {
        const userId = rootGetters['auth/userId']
        await this.$api.collectionItem.unlikeComment(item.ownerId, item.id, comment.id)
        await UserLike.delete(record => record.resourceId === comment.id && record.userId === userId)
        await applyCommentLikeState(comment, false)
      }
    },

    // ==================== PIN ====================
    async pinItem({ commit }, { collectionId, itemId, position }) {
      const result = await this.$api.collectionItem.pinItem(collectionId, itemId, { position })
      await CollectionItem.insertOrUpdate({ data: result })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    async unpinItem({ commit }, { collectionId, itemId }) {
      await this.$api.collectionItem.unpinItem(collectionId, itemId)
      await CollectionItem.update({
        where: itemId,
        data: { pinPosition: null },
      })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return true
    },

    // ==================== COLLECTIONS ====================
    async addToCollections(_, { item, ...data }) {
      return this.$api.collectionItem.addToCollections(item.ownerId, item.id, data)
    },

    async save({ rootGetters }, { item, ...data }) {
      try {
        await item.setLoading('save', true)

        const privateCollectionId = rootGetters['auth/collectionId']
        // { [privateCollectionId]: newItemId }
        return await this.$api.collectionItem.addToCollections(item.ownerId, item.id, {
          collectionIds: [privateCollectionId],
          ...data,
          isPrivate: true,
        })
      } finally {
        await item.setLoading('save', false)
      }
    },
  },
}
